import { type ReactNode, useMemo } from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { DynamicTextPropType } from '../../text/DynamicText';
import { Icon } from '../../assets/Icon/Icon';
import { ControlProvider } from '../../controls/shared/context';
import { styled } from '../../stitches.config';
import { DynamicText } from '../../text/DynamicText';

const EmptyStateIcon = styled(Icon, {
  width: '$24',
  height: '$24',

  variants: {
    size: {
      small: {
        width: '$18',
        height: '$18',
      },
      medium: {
        width: '$24',
        height: '$24',
      },
      large: {
        width: '$48',
        height: '$48',
      },
    },
  },
});
EmptyStateIcon.displayName = 'EmptyStateIcon';

const EmptyStateHeading = styled(DynamicText, {
  textAlign: 'center',
});
EmptyStateHeading.displayName = 'EmptyStateHeading';

const EmptyStateSubheading = styled(DynamicText, {
  textAlign: 'center',
});
EmptyStateSubheading.displayName = 'EmptyStateSubheading';

const EmptyStateHeader = styled('div', {
  vStack: '$0',
});
EmptyStateHeader.displayName = 'EmptyStateHeader';

const EmptyStateContent = styled('div', {
  display: 'flex',

  variants: {
    size: {
      small: {
        vStack: '$4',
      },
      medium: {
        vStack: '$12',
        flex: '70%',
      },
      large: {
        vStack: '$16',
        flex: '70%',
        justifyContent: 'center',
      },
    },
  },
});
EmptyStateContent.displayName = 'EmptyStateContent';

const EmptyStateBuffer = styled('div', {
  display: 'flex',
  flex: '30%',
});
EmptyStateBuffer.displayName = 'EmptyStateBuffer';

const EmptyStateContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',

  variants: {
    size: {
      small: {
        padding: '$10 $16',
      },
      medium: {
        minHeight: '$180',
        padding: '$20 $32',
      },
      large: {
        minHeight: '$180',
        padding: '$24 $36',
        justifyContent: 'center',
      },
    },
  },
});
EmptyStateContainer.displayName = 'EmptyStateContainer';

export interface EmptyStateProps {
  heading: ReactNode;
  subheading?: ReactNode;
  icon?: IconName;
  action?: ReactNode;
  size?: 'small' | 'medium' | 'large';
}

export function EmptyState({
  heading,
  subheading,
  icon,
  action,
  size = 'medium',
  ...remaining
}: EmptyStateProps) {
  const headingType = useMemo((): DynamicTextPropType => {
    switch (size) {
      case 'small':
        return 'small';
      case 'medium':
        return 'body';
      case 'large':
        return 'heading';
      default:
        return 'body';
    }
  }, [size]);

  const subHeadingType = useMemo((): DynamicTextPropType => {
    switch (size) {
      case 'small':
        return 'small';
      case 'medium':
        return 'body';
      case 'large':
        return 'subheading';
      default:
        return 'body';
    }
  }, [size]);

  return (
    <EmptyStateContainer {...remaining} size={size}>
      {size === 'medium' && <EmptyStateBuffer />}
      <EmptyStateContent size={size}>
        {icon && <EmptyStateIcon size={size} icon={icon} variant="neutral" />}
        <EmptyStateHeader>
          <EmptyStateHeading context="heading" variant="neutral" weight="bold" type={headingType}>
            {heading}
          </EmptyStateHeading>
          {subheading && (
            <EmptyStateSubheading context="body" variant="neutral" type={subHeadingType}>
              {subheading}
            </EmptyStateSubheading>
          )}
        </EmptyStateHeader>
        {action && (
          <ControlProvider value={{ appearance: 'vivid', size: 'small', variant: 'secondary' }}>
            {action}
          </ControlProvider>
        )}
      </EmptyStateContent>
    </EmptyStateContainer>
  );
}
